// frontend/src/pages/ControlRoom/ControlRoom.jsx
import { useCallback, useMemo, useState } from "react";
import "./control-room.css";

import {
  Settings,
  Brain,
  CheckCircle2,
  User,
  PlugZap,
  X,
} from "lucide-react";

import MemoriesPanel from "@/components/MemoriesPanel";
import CompletedTasksPanel from "./CompletedTasksPanel";

const PANELS = {
  vox_recall: {
    key: "vox_recall",
    label: "Vox Recall",
    Icon: Brain,
  },
  completed_tasks: {
    key: "completed_tasks",
    label: "Completed Tasks",
    Icon: CheckCircle2,
  },
  profile: {
    key: "profile",
    label: "Profile",
    Icon: User,
  },
  integrations: {
    key: "integrations",
    label: "Integrations",
    Icon: PlugZap,
  },
};

export default function ControlRoom() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activePanel, setActivePanel] = useState(null);
  const [pressedHex, setPressedHex] = useState(null);

  const children = useMemo(
    () => [
      PANELS.vox_recall,
      PANELS.completed_tasks,
      PANELS.profile,
      PANELS.integrations,
    ],
    []
  );

  const closePanel = useCallback(() => setActivePanel(null), []);
  const collapseAll = useCallback(() => {
    setActivePanel(null);
    setIsExpanded(false);
  }, []);

  const toggleCenterHex = useCallback(() => {
    setActivePanel(null);
    setIsExpanded((v) => !v);
  }, []);

  const openPanel = useCallback((key) => {
    setActivePanel(key);
  }, []);

  const panelTitle = activePanel ? PANELS[activePanel]?.label : "";

  return (
    <div className="control-room-root">
      <div className="control-room-stage">
        <div className={`hex-cluster ${isExpanded ? "is-expanded" : ""}`}>
          {children.map(({ key, label, Icon }) => (
            <button
              key={key}
              type="button"
              className={[
                "hex",
                "hex-child",
                `hex-child-${key}`,
                pressedHex === key ? "is-pressed" : "",
              ].join(" ")}
              onMouseDown={() => setPressedHex(key)}
              onMouseUp={() => setPressedHex(null)}
              onMouseLeave={() => setPressedHex(null)}
              onClick={() => openPanel(key)}
              aria-label={label}
            >
              <div className="hex-inner">
                <Icon className="hex-glyph" />
                <div className="hex-label">{label}</div>
              </div>
            </button>
          ))}

          <button
            type="button"
            className={[
              "hex",
              "hex-center",
              pressedHex === "center" ? "is-pressed" : "",
            ].join(" ")}
            onMouseDown={() => setPressedHex("center")}
            onMouseUp={() => setPressedHex(null)}
            onMouseLeave={() => setPressedHex(null)}
            onClick={toggleCenterHex}
            aria-label="Settings"
          >
            <div className="hex-inner">
              <Settings className="hex-glyph" />
              <div className="hex-label">Settings</div>
            </div>
          </button>
        </div>
      </div>

      {activePanel && (
        <div
          className="control-room-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closePanel();
          }}
        >
          <div className="control-room-modal console-card">
            <div className="control-room-modal-header">
              <div className="control-room-modal-title">{panelTitle}</div>

              <div className="control-room-modal-actions">
                <button
                  type="button"
                  className="icon-btn"
                  onClick={closePanel}
                >
                  <X size={18} />
                </button>

                <button
                  type="button"
                  className="ghost-btn"
                  onClick={collapseAll}
                >
                  Collapse
                </button>
              </div>
            </div>

            <div className="control-room-modal-body">
              {activePanel === "vox_recall" && <MemoriesPanel />}
              {activePanel === "completed_tasks" && <CompletedTasksPanel />}

              {activePanel === "profile" && (
                <div className="placeholder-panel">
                  <div className="placeholder-title">Profile</div>
                  <div className="placeholder-text">
                    Coming soon.
                  </div>
                </div>
              )}

              {activePanel === "integrations" && (
                <div className="placeholder-panel">
                  <div className="placeholder-title">Integrations</div>
                  <div className="placeholder-text">
                    Coming soon.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

